import type { IPlaybackHeartbeatBody, TPlaybackHeartbeatBody } from '@lucimark/shared/models/playback-heartbeat'
import type { ZodIssue } from 'zod'
import {

  playbackHeartbeatBodySchema

} from '@lucimark/shared/models/playback-heartbeat'
import { playerDiagnosticsSnapshotSchema } from '@lucimark/shared/models/player-diagnostics'
import z from 'zod'

/** Top-level heartbeat fields validated independently (proof-of-play keys handled separately). */
const HEARTBEAT_SCALAR_KEYS = [
  'storageUsedPercent',
  'hardwareCapacity',
  'version',
  'webVersion',
  'screenRotation',
  'screenOrientation',
  'screenWidthPx',
  'screenHeightPx',
  'currentStatusOverlayVisible',
  'timersOverlayVisible',
  'androidIdPrefix',
  'deviceOwner',
  'accessibilityEnabled',
  'powerSource',
  'batteryPresent',
  'batteryPercent',
  'batteryCharging',
  'brightnessPercent',
  'brightnessMode',
  'brightnessControl',
  'brightnessMethod',
  'writeSettingsGranted',
  'ambientLightSupported',
  'ambientLux',
  'audienceAggregate',
  'audienceVideoInputDevices',
  'utcOffsetMinutes',
  'segmentResolvedLoopSeconds',
  'weeklyAvailabilityPlaybackSuppressed',
  'playbackTimerStuckSuspected',
  'playbackTimerStuckOverrunMs',
  'playbackTimerStuckFrameId',
  'playbackTimerStuckVisibleDwellMs',
  'playbackTimerStuckTimingFlatlineMs',
  'lastBootAt',
  'lastBootWasCleanShutdown',
  'lastBootHadBatteryOrPowerEvent',
  'lastBootKind'
] as const satisfies ReadonlyArray<keyof IPlaybackHeartbeatBody>

function logPlaybackHeartbeatBodySanitized(issues: ZodIssue[], merged: TPlaybackHeartbeatBody): void {
  const keptKeys = Object.keys(merged)
  console.warn(
    JSON.stringify({
      tag: 'playback_heartbeat_body_sanitized',
      issueCount: issues.length,
      /** Cap size so logs stay usable in the field. */
      issues: issues.slice(0, 25),
      keptKeys,
      keptFieldCount: keptKeys.length
    })
  )
}

function buildHeartbeatCandidate(opts?: IPlaybackHeartbeatBody): Partial<IPlaybackHeartbeatBody> {
  const candidate: Partial<IPlaybackHeartbeatBody> = {}
  if (opts?.storageUsedPercent != null) {
    candidate.storageUsedPercent = opts.storageUsedPercent
  }
  if (opts?.hardwareCapacity != null) {
    candidate.hardwareCapacity = opts.hardwareCapacity
  }
  if (opts?.version != null && opts.version !== '') {
    candidate.version = opts.version
  }
  if (opts?.webVersion != null && opts.webVersion !== '') {
    candidate.webVersion = opts.webVersion
  }
  if (opts?.screenRotation != null) {
    candidate.screenRotation = opts.screenRotation
  }
  if (opts?.screenOrientation != null) {
    candidate.screenOrientation = opts.screenOrientation
  }
  if (opts?.screenWidthPx != null && opts?.screenHeightPx != null) {
    candidate.screenWidthPx = opts.screenWidthPx
    candidate.screenHeightPx = opts.screenHeightPx
  }
  if (opts?.currentStatusOverlayVisible !== undefined) {
    candidate.currentStatusOverlayVisible = opts.currentStatusOverlayVisible
  }
  if (opts?.timersOverlayVisible !== undefined) {
    candidate.timersOverlayVisible = opts.timersOverlayVisible
  }
  if (opts?.androidIdPrefix != null && opts.androidIdPrefix !== '') {
    candidate.androidIdPrefix = opts.androidIdPrefix
  }
  if (opts?.deviceOwner !== undefined) {
    candidate.deviceOwner = opts.deviceOwner
  }
  if (opts?.accessibilityEnabled !== undefined) {
    candidate.accessibilityEnabled = opts.accessibilityEnabled
  }
  if (opts?.powerSource !== undefined) {
    candidate.powerSource = opts.powerSource
  }
  if (opts?.batteryPresent !== undefined) {
    candidate.batteryPresent = opts.batteryPresent
  }
  if (opts?.batteryPercent !== undefined) {
    candidate.batteryPercent = opts.batteryPercent
  }
  if (opts?.batteryCharging !== undefined) {
    candidate.batteryCharging = opts.batteryCharging
  }
  if (opts?.brightnessPercent !== undefined) {
    candidate.brightnessPercent = opts.brightnessPercent
  }
  if (opts?.brightnessMode !== undefined) {
    candidate.brightnessMode = opts.brightnessMode
  }
  if (opts?.brightnessControl !== undefined) {
    candidate.brightnessControl = opts.brightnessControl
  }
  if (opts?.brightnessMethod !== undefined) {
    candidate.brightnessMethod = opts.brightnessMethod
  }
  if (opts?.writeSettingsGranted !== undefined) {
    candidate.writeSettingsGranted = opts.writeSettingsGranted
  }
  if (opts?.ambientLightSupported !== undefined) {
    candidate.ambientLightSupported = opts.ambientLightSupported
  }
  if (opts?.ambientLux !== undefined) {
    candidate.ambientLux = opts.ambientLux
  }
  if (opts?.utcOffsetMinutes != null) {
    candidate.utcOffsetMinutes = opts.utcOffsetMinutes
  }
  if (opts?.segmentResolvedLoopSeconds != null) {
    candidate.segmentResolvedLoopSeconds = opts.segmentResolvedLoopSeconds
  }
  if (opts?.assetPlays != null && opts.assetPlays.length > 0) {
    candidate.assetPlays = opts.assetPlays
    if (opts.playlistUrn != null) {
      candidate.playlistUrn = opts.playlistUrn
    }
    if (opts.playerGeohash != null && opts.playerGeohash !== '') {
      candidate.playerGeohash = opts.playerGeohash
    }
    if (opts.orderId != null && opts.orderId !== '') {
      candidate.orderId = opts.orderId
    }
    if (opts.networkOperatorId != null && opts.networkOperatorId !== '') {
      candidate.networkOperatorId = opts.networkOperatorId
    }
  }
  if (opts?.audienceAggregate != null) {
    candidate.audienceAggregate = opts.audienceAggregate
  }
  if (opts?.audienceVideoInputDevices != null && opts.audienceVideoInputDevices.length > 0) {
    candidate.audienceVideoInputDevices = opts.audienceVideoInputDevices
  }
  if (opts?.weeklyAvailabilityPlaybackSuppressed !== undefined) {
    candidate.weeklyAvailabilityPlaybackSuppressed = opts.weeklyAvailabilityPlaybackSuppressed
  }
  if (opts?.playbackTimerStuckSuspected !== undefined) {
    candidate.playbackTimerStuckSuspected = opts.playbackTimerStuckSuspected
  }
  if (opts?.playbackTimerStuckOverrunMs != null) {
    candidate.playbackTimerStuckOverrunMs = opts.playbackTimerStuckOverrunMs
  }
  if (opts?.playbackTimerStuckFrameId != null && opts.playbackTimerStuckFrameId !== '') {
    candidate.playbackTimerStuckFrameId = opts.playbackTimerStuckFrameId
  }
  if (opts?.playbackTimerStuckVisibleDwellMs != null) {
    candidate.playbackTimerStuckVisibleDwellMs = opts.playbackTimerStuckVisibleDwellMs
  }
  if (opts?.playbackTimerStuckTimingFlatlineMs != null) {
    candidate.playbackTimerStuckTimingFlatlineMs = opts.playbackTimerStuckTimingFlatlineMs
  }
  if (opts?.playerDiagnostics != null) {
    candidate.playerDiagnostics = opts.playerDiagnostics
  }
  if (opts?.lastBootAt != null && opts.lastBootAt !== '') {
    candidate.lastBootAt = opts.lastBootAt
  }
  if (opts?.lastBootWasCleanShutdown !== undefined) {
    candidate.lastBootWasCleanShutdown = opts.lastBootWasCleanShutdown
  }
  if (opts?.lastBootHadBatteryOrPowerEvent !== undefined) {
    candidate.lastBootHadBatteryOrPowerEvent = opts.lastBootHadBatteryOrPowerEvent
  }
  if (opts?.lastBootKind != null) {
    candidate.lastBootKind = opts.lastBootKind
  }
  return candidate
}

function assignSanitizedScalarFields(
  candidate: Partial<IPlaybackHeartbeatBody>,
  merged: Record<string, unknown>
): void {
  const shape = playbackHeartbeatBodySchema.shape
  for (const key of HEARTBEAT_SCALAR_KEYS) {
    if (!(key in candidate)) {
      continue
    }
    const value = candidate[key]
    if (
      value === undefined
      && key !== 'currentStatusOverlayVisible'
      && key !== 'timersOverlayVisible'
      && key !== 'weeklyAvailabilityPlaybackSuppressed'
      && key !== 'playbackTimerStuckSuspected'
      && key !== 'lastBootWasCleanShutdown'
      && key !== 'lastBootHadBatteryOrPowerEvent'
      && key !== 'lastBootKind'
    ) {
      continue
    }
    const fieldSchema = z.object({ [key]: shape[key] })
    const parsed = fieldSchema.safeParse({ [key]: value })
    if (!parsed.success) {
      continue
    }
    const out = parsed.data as Record<string, unknown>
    merged[key] = out[key]
  }
}

function assignSanitizedProofOfPlayOrderAndOperator(
  candidate: Partial<IPlaybackHeartbeatBody>,
  merged: Record<string, unknown>
): void {
  const { orderId, networkOperatorId } = playbackHeartbeatBodySchema.shape
  if (candidate.orderId != null && candidate.orderId !== '') {
    const pr = orderId.safeParse(candidate.orderId)
    if (pr.success) {
      merged.orderId = pr.data
    }
  }
  if (candidate.networkOperatorId != null && candidate.networkOperatorId !== '') {
    const pr = networkOperatorId.safeParse(candidate.networkOperatorId)
    if (pr.success) {
      merged.networkOperatorId = pr.data
    }
  }
}

function assignSanitizedProofOfPlay(
  candidate: Partial<IPlaybackHeartbeatBody>,
  merged: Record<string, unknown>
): void {
  if (candidate.assetPlays == null || candidate.assetPlays.length === 0) {
    return
  }
  /** Zod 4: `.pick()` on `playbackHeartbeatBodySchema` fails because the object uses `.superRefine()`. */
  const { assetPlays, playlistUrn, playerGeohash } = playbackHeartbeatBodySchema.shape
  const pick3 = z.object({ assetPlays, playlistUrn, playerGeohash })
  const pick2 = z.object({ assetPlays, playlistUrn })
  const pick1 = z.object({ assetPlays })

  const base3 = {
    assetPlays: candidate.assetPlays,
    ...(candidate.playlistUrn != null ? { playlistUrn: candidate.playlistUrn } : {}),
    ...(candidate.playerGeohash != null && candidate.playerGeohash !== ''
      ? { playerGeohash: candidate.playerGeohash }
      : {})
  }
  let r = pick3.safeParse(base3)
  if (r.success) {
    Object.assign(merged, r.data)
    assignSanitizedProofOfPlayOrderAndOperator(candidate, merged)
    return
  }
  r = pick2.safeParse({
    assetPlays: candidate.assetPlays,
    ...(candidate.playlistUrn != null ? { playlistUrn: candidate.playlistUrn } : {})
  })
  if (r.success) {
    Object.assign(merged, r.data)
    assignSanitizedProofOfPlayOrderAndOperator(candidate, merged)
    return
  }
  r = pick1.safeParse({ assetPlays: candidate.assetPlays })
  if (r.success) {
    Object.assign(merged, r.data)
  }
  assignSanitizedProofOfPlayOrderAndOperator(candidate, merged)
}

function assignSanitizedPlayerDiagnostics(
  candidate: Partial<IPlaybackHeartbeatBody>,
  merged: Record<string, unknown>
): void {
  if (candidate.playerDiagnostics == null) {
    return
  }
  const parsed = playerDiagnosticsSnapshotSchema.safeParse(candidate.playerDiagnostics)
  if (parsed.success) {
    merged.playerDiagnostics = parsed.data
  }
}

function mergeSanitizedHeartbeatBody(candidate: Partial<IPlaybackHeartbeatBody>): TPlaybackHeartbeatBody {
  const merged: Record<string, unknown> = {}
  assignSanitizedScalarFields(candidate, merged)
  assignSanitizedProofOfPlay(candidate, merged)
  assignSanitizedPlayerDiagnostics(candidate, merged)
  const final = playbackHeartbeatBodySchema.safeParse(merged)
  if (!final.success) {
    console.warn(
      JSON.stringify({
        tag: 'playback_heartbeat_body_merge_unexpected',
        issueCount: final.error.issues.length,
        issues: final.error.issues.slice(0, 25)
      })
    )
    return {}
  }
  return final.data
}

/**
 * Maps compositor/service options into the POST /playback-heartbeat JSON body and validates
 * with the shared Zod schema. Invalid fields are dropped after a `console.warn` so the heartbeat
 * still ships with the valid subset.
 */
export function buildPlaybackHeartbeatRequestBody(opts?: IPlaybackHeartbeatBody): TPlaybackHeartbeatBody {
  const candidate = buildHeartbeatCandidate(opts)
  const full = playbackHeartbeatBodySchema.safeParse(candidate)
  if (full.success) {
    return full.data
  }
  const sanitized = mergeSanitizedHeartbeatBody(candidate)
  logPlaybackHeartbeatBodySanitized(full.error.issues, sanitized)
  return sanitized
}
